/**
 * P4-09: 通用 Dialog 组件与 DialogProvider。
 *
 * 特性：
 * - 遮罩层 + 内容面板，点击遮罩或按 Escape 关闭最上层 dialog；
 * - 支持多个 dialog 同时打开，维护栈式状态；
 * - `disableBackdropClick` 可禁用遮罩关闭；
 * - 移动端 <768px 默认全屏，桌面端居中限制尺寸；
 * - aria 属性：role="dialog"、aria-modal="true"、aria-labelledby。
 *
 * 使用：
 * ```tsx
 * const { openDialog, closeDialog, closeTopDialog, closeAllDialogs } = useDialog();
 * ```
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export interface DialogConfig {
  id: string;
  title?: string;
  children: ReactNode;
  disableBackdropClick?: boolean;
}

interface DialogContextValue {
  stack: readonly DialogConfig[];
  openDialog(config: DialogConfig): void;
  closeDialog(id: string): void;
  closeTopDialog(): void;
  closeAllDialogs(): void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog 必须在 <DialogProvider> 内使用");
  }
  return ctx;
}

interface DialogProviderProps {
  children?: ReactNode;
}

export function DialogProvider({ children }: DialogProviderProps) {
  const [stack, setStack] = useState<DialogConfig[]>([]);

  const openDialog = useCallback((config: DialogConfig) => {
    setStack((prev) => {
      const idx = prev.findIndex((d) => d.id === config.id);
      if (idx >= 0) {
        // 同名 dialog 替换到栈顶，避免重复
        const next = prev.slice();
        next.splice(idx, 1);
        return [...next, config];
      }
      return [...prev, config];
    });
  }, []);

  const closeDialog = useCallback((id: string) => {
    setStack((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const closeTopDialog = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const closeAllDialogs = useCallback(() => {
    setStack([]);
  }, []);

  const value = useMemo(
    () => ({
      stack,
      openDialog,
      closeDialog,
      closeTopDialog,
      closeAllDialogs,
    }),
    [stack, openDialog, closeDialog, closeTopDialog, closeAllDialogs],
  );

  return (
    <DialogContext.Provider value={value}>
      {children}
      <DialogStack stack={stack} onCloseTop={closeTopDialog} />
    </DialogContext.Provider>
  );
}

interface DialogStackProps {
  stack: readonly DialogConfig[];
  onCloseTop: () => void;
}

function DialogStack({ stack, onCloseTop }: DialogStackProps) {
  const top = stack[stack.length - 1];

  useEffect(() => {
    if (!top) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseTop();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [top, onCloseTop]);

  if (stack.length === 0) {
    return null;
  }

  return (
    <>
      {stack.map((dialog, index) => (
        <DialogShell
          key={dialog.id}
          dialog={dialog}
          index={index}
          isTop={index === stack.length - 1}
          onCloseTop={onCloseTop}
        />
      ))}
    </>
  );
}

interface DialogShellProps {
  dialog: DialogConfig;
  index: number;
  isTop: boolean;
  onCloseTop: () => void;
}

function DialogShell({ dialog, index, isTop, onCloseTop }: DialogShellProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!isTop) {
      return;
    }
    if (dialog.disableBackdropClick) {
      return;
    }
    if (e.target === e.currentTarget) {
      onCloseTop();
    }
  };

  // 焦点陷阱：Tab 在面板内循环
  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !panelRef.current) {
      return;
    }
    // eslint-disable-next-line quotes
    const focusable = panelRef.current.querySelectorAll<HTMLElement>(`button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])`);
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const titleId = `dialog-title-${dialog.id}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center md:items-center md:p-4"
      style={{ zIndex: 1000 + index }}
      onClick={handleBackdropClick}
      role="presentation"
      aria-hidden={!isTop}
    >
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialog.title ? titleId : undefined}
        className="relative flex w-full flex-col bg-white text-black shadow-lg md:max-h-[80vh] md:w-auto md:min-w-[320px] md:max-w-2xl md:rounded-lg"
        style={{ height: "auto", maxHeight: "100vh" }}
        onKeyDown={handlePanelKeyDown}
      >
        {dialog.title && (
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 id={titleId} className="text-lg font-semibold">
              {dialog.title}
            </h2>
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center text-2xl leading-none text-gray-500 hover:text-gray-800"
              aria-label="Close"
              onClick={onCloseTop}
            >
              ×
            </button>
          </div>
        )}
        <div className="overflow-auto p-4">{dialog.children}</div>
      </div>
    </div>
  );
}

export default DialogShell;
